import { useState } from 'react';
import { Popover, Switch, message } from 'antd';
import {
  GroupOutlined,
  EyeOutlined,
  LockOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import useEditorUiStore from '../../../stores/editorUiStore';
import useLayerStore from '../../../stores/layerStore';
import useSceneStore from '../../../stores/sceneStore';
import useDashboardStore from '../../../stores/dashboardStore';

/** 可见性控制：集中列出 3D 图层与 2D 大屏组件的显隐开关 */
function VisibilityContent() {
  const layers = useLayerStore((s) => s.layers);
  const toggleVisibility = useLayerStore((s) => s.toggleVisibility);
  const components = useDashboardStore((s) => s.components);
  const updateComponent = useDashboardStore((s) => s.updateComponent);
  const dashboardId = useEditorUiStore((s) => s.dashboardId);

  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' };
  const nameStyle: React.CSSProperties = { flex: 1, fontSize: 12, color: '#ddd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <div data-testid="fusion-vis-popover" style={{ width: 236, maxHeight: 380, overflow: 'auto' }}>
      <div style={{ fontSize: 12, color: '#8b8ba7', marginBottom: 4 }}>3D 图层</div>
      {layers.length === 0 && <div style={{ fontSize: 12, color: '#666', padding: '2px 0' }}>暂无图层</div>}
      {layers.map((l) => (
        <div key={l.id} data-testid={`fusion-vis-layer-${l.id}`} style={rowStyle}>
          <span style={nameStyle} title={l.name}>{l.name}</span>
          <Switch
            size="small"
            checked={l.visible !== false}
            aria-label="图层显隐"
            onChange={() => toggleVisibility(l.id)}
          />
        </div>
      ))}
      <div style={{ fontSize: 12, color: '#8b8ba7', margin: '8px 0 4px' }}>2D 大屏组件</div>
      {!dashboardId && <div style={{ fontSize: 12, color: '#666', padding: '2px 0' }}>未关联大屏</div>}
      {dashboardId && components.length === 0 && <div style={{ fontSize: 12, color: '#666', padding: '2px 0' }}>暂无组件</div>}
      {dashboardId && components.map((c) => {
        const visible = ((c as any).config?.visible ?? (c as any).base?.visible ?? true) !== false;
        const name = String((c as any).base?.title || (c as any).config?.title || c.type);
        return (
          <div key={c.id} data-testid={`fusion-vis-comp-${c.id}`} style={rowStyle}>
            <span style={nameStyle} title={name}>{name}</span>
            <Switch
              size="small"
              checked={visible}
              aria-label="组件显隐"
              onChange={() => updateComponent(c.id, { config: { ...(c.config || {}), visible: !visible } })}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function FusionMenu() {
  const [open, setOpen] = useState(false);
  const [visOpen, setVisOpen] = useState(false);

  const runLayerFusion = () => {
    useEditorUiStore.getState().setActivePanel('layers');
    useEditorUiStore.getState().highlightLayerCard();
    // 等面板渲染后滚动到卡片（不存在则忽略）
    setTimeout(() => {
      document.querySelector('[data-testid="dash-visual-layer"]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
    setOpen(false);
  };

  const runOverlayEdit = () => {
    const st = useEditorUiStore.getState();
    if (!st.dashboardId) {
      st.setDashboardBindOpen(true);
      message.info('请先关联 / 导入大屏');
    } else {
      st.setOverlayEditMode(true);
    }
    setOpen(false);
  };

  const runLock = () => {
    const scene = useSceneStore.getState();
    const layer = useLayerStore.getState();
    if (scene.selectedModelId) {
      scene.toggleLocked(scene.selectedModelId);
      message.success('已切换选中模型的锁定状态');
    } else if (layer.selectedLayerId) {
      layer.toggleLock(layer.selectedLayerId);
      message.success('已切换选中图层的锁定状态');
    } else {
      message.info('请先选中图层或模型（左侧「图层」/场景中选中）');
    }
    setOpen(false);
  };

  const itemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
    fontSize: 12, color: '#e0e0e0', cursor: 'pointer', borderRadius: 4, userSelect: 'none',
  };

  const content = (
    <div data-testid="fusion-menu" style={{ width: 236 }}>
      <div
        data-testid="fusion-menu-layer-fusion"
        style={itemStyle}
        onClick={runLayerFusion}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,158,255,.14)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <GroupOutlined style={{ color: '#52c41a' }} /> 图层融合设置
      </div>
      <div
        data-testid="fusion-menu-overlay-edit"
        style={itemStyle}
        onClick={runOverlayEdit}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,158,255,.14)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <ExpandOutlined style={{ color: '#4e9eff' }} /> 大屏叠加编辑
      </div>
      <div style={{ height: 1, background: '#30475e', margin: '4px 0' }} />
      <div
        data-testid="fusion-menu-visibility"
        style={itemStyle}
        onClick={(e) => { e.stopPropagation(); setVisOpen((v) => !v); }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,158,255,.14)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <EyeOutlined style={{ color: '#faad14' }} /> 可见性控制
      </div>
      {visOpen && (
        <div style={{ borderTop: '1px dashed #30475e', marginTop: 4, paddingTop: 6 }} onClick={(e) => e.stopPropagation()}>
          <VisibilityContent />
        </div>
      )}
      <div
        data-testid="fusion-menu-lock"
        style={itemStyle}
        onClick={runLock}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(78,158,255,.14)'; }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        <LockOutlined style={{ color: '#ff7875' }} /> 锁定图层
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(v) => { setOpen(v); if (!v) setVisOpen(false); }}
      trigger={['click']}
      placement="bottomLeft"
      arrow={false}
      title={null}
      content={content}
      styles={{ content: { background: '#1a1a2e', border: '1px solid #30475e', borderRadius: 8, padding: 8 } }}
    >
      <span style={{ cursor: 'pointer', padding: '0 8px' }} data-testid="fusion-menu-trigger">
        融合▼
      </span>
    </Popover>
  );
}
